import React from "react";
import { Box, Divider, Typography } from "@mui/material";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

import { Colors } from "../../../configs";
import CustomModal from "../../common/custom-modal/custom-modal";
import { CustomButton } from "../../atoms";

export type UnfinishedOrder = {
  id: string;
  customerName: string;
  pieces?: number;
  amount?: string;
  lastEdited?: string;
};

export interface IUnfinishedOrdersModalProps {
  open?: boolean;
  orders?: UnfinishedOrder[];
  onClose?: () => void;
  onResumeOrder?: (order: UnfinishedOrder) => void;
  onViewOrder?: (order: UnfinishedOrder) => void;
}

export default function UnfinishedOrdersModal({
  open = false,
  orders = [],
  onClose = () => {},
  onResumeOrder = () => {},
  onViewOrder = () => {},
}: IUnfinishedOrdersModalProps) {
  const primaryOrder = orders[0];
  const remainingOrders = orders.slice(1);

  return (
    <CustomModal
      title="Unfinished Orders"
      openModal={open}
      onClose={onClose}
      width="52rem"
      height="auto"
      titleSize="2rem"
      titleColor={Colors.ERROR_RED}
      disableBackdropClick
      sx={{ borderRadius: "1rem" }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: "1.5rem", paddingTop: "0.5rem" }}>
        {/* Warning banner */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "1rem",
            background: Colors.NOTIFIC_RED,
            borderRadius: "0.714285714rem",
            padding: "1.14rem 1.42rem",
          }}
        >
          <WarningAmberRoundedIcon sx={{ fontSize: "1.8rem", color: Colors.ERROR_RED, flexShrink: 0 }} />
          <Typography sx={{ fontSize: "1.14rem", color: Colors.ERROR_RED, fontWeight: 500 }}>
            Please complete all the pending orders before logging out.
          </Typography>
        </Box>

        {/* Most recent order */}
        {primaryOrder && (
          <Box
            sx={{
              background: Colors.BACKGROUND_GRAY,
              borderRadius: "0.714285714rem",
              padding: "1.42rem",
              border: `0.0714rem solid ${Colors.ACCENT_GRAY_DISABLED}`,
              position: "relative",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                top: "-0.75rem",
                left: "1.14rem",
                background: Colors.PRIMARY,
                color: Colors.WHITE,
                fontSize: "0.85rem",
                fontWeight: 700,
                letterSpacing: "0.04em",
                borderRadius: "0.5rem",
                px: "0.85rem",
                py: "0.25rem",
              }}
            >
              MOST RECENT
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "1rem" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: "1rem", minWidth: 0 }}>
                <Box
                  sx={{
                    width: "3rem",
                    height: "3rem",
                    borderRadius: "50%",
                    background: "#E6F0FF",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <PersonOutlineOutlinedIcon sx={{ fontSize: "1.6rem", color: Colors.SECONDARY }} />
                </Box>

                <Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: "0.75rem", flexWrap: "wrap" }}>
                    <Typography sx={{ fontSize: "1.28rem", fontWeight: 700, color: Colors.TEXT_GRAY_DARK }}>
                      {primaryOrder.customerName}
                    </Typography>
                    {primaryOrder.pieces != null && (
                      <Box
                        sx={{
                          background: "#DDF4E7",
                          color: Colors.PRIMARY,
                          fontSize: "0.92rem",
                          fontWeight: 600,
                          borderRadius: "1rem",
                          px: "0.75rem",
                          py: "0.2rem",
                        }}
                      >
                        {primaryOrder.pieces} pcs
                      </Box>
                    )}
                    {primaryOrder.amount && (
                      <Typography sx={{ fontSize: "1.14rem", fontWeight: 600, color: Colors.TEXT_GRAY_DARK }}>
                        {primaryOrder.amount}
                      </Typography>
                    )}
                  </Box>
                  {primaryOrder.lastEdited && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: "0.35rem", mt: "0.35rem" }}>
                      <AccessTimeOutlinedIcon sx={{ fontSize: "1rem", color: Colors.MAIN_GRAY }} />
                      <Typography sx={{ fontSize: "1rem", color: Colors.MAIN_GRAY }}>
                        {primaryOrder.lastEdited}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>

              <CustomButton
                onClick={() => onResumeOrder(primaryOrder)}
                sx={{
                  height: "3.14rem",
                  px: "1.42rem",
                  fontSize: "1.14rem",
                  fontWeight: 600,
                  borderRadius: "0.714285714rem",
                  flexShrink: 0,
                  display: "flex",
                  gap: "0.35rem",
                }}
              >
                <ChevronRightIcon sx={{ fontSize: "1.3rem" }} />
                Resume Order
              </CustomButton>
            </Box>
          </Box>
        )}

        {/* Remaining orders */}
        {remainingOrders.length > 0 && (
          <Box>
            <Typography sx={{ fontSize: "1.14rem", fontWeight: 600, color: Colors.MAIN_GRAY, mb: "0.75rem" }}>
              Other Pending Orders ({remainingOrders.length})
            </Typography>

            <Box
              sx={{
                border: `0.0714rem solid ${Colors.ACCENT_GRAY_DISABLED}`,
                borderRadius: "0.714285714rem",
                overflow: "hidden",
              }}
            >
              {remainingOrders.map((order, index) => (
                <Box key={order.id}>
                  {index > 0 && <Divider sx={{ borderColor: Colors.ACCENT_GRAY_DISABLED }} />}
                  <Box
                    onClick={() => onViewOrder(order)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: "1rem",
                      px: "1.14rem",
                      py: "0.857rem",
                      cursor: "pointer",
                      background: Colors.WHITE,
                      "&:hover": { background: Colors.BACKGROUND_GRAY },
                      transition: "background 0.15s ease",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: "0.85rem", minWidth: 0 }}>
                      <Box
                        sx={{
                          width: "2.5rem",
                          height: "2.5rem",
                          borderRadius: "50%",
                          background: Colors.BACKSHADE_GRAY,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <PersonOutlineOutlinedIcon sx={{ fontSize: "1.3rem", color: Colors.TEXT_GRAY_DARK }} />
                      </Box>

                      <Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: "0.6rem", flexWrap: "wrap" }}>
                          <Typography sx={{ fontSize: "1.14rem", fontWeight: 600, color: Colors.TEXT_GRAY_DARK }}>
                            {order.customerName}
                          </Typography>
                          {order.pieces != null && (
                            <Box
                              sx={{
                                background: "#E6F0FF",
                                color: Colors.SECONDARY,
                                fontSize: "0.85rem",
                                fontWeight: 600,
                                borderRadius: "1rem",
                                px: "0.6rem",
                                py: "0.15rem",
                              }}
                            >
                              {order.pieces} pcs
                            </Box>
                          )}
                          {order.amount && (
                            <Typography sx={{ fontSize: "1rem", fontWeight: 500, color: Colors.TEXT_GRAY_DARK }}>
                              {order.amount}
                            </Typography>
                          )}
                        </Box>
                        {order.lastEdited && (
                          <Box sx={{ display: "flex", alignItems: "center", gap: "0.3rem", mt: "0.25rem" }}>
                            <AccessTimeOutlinedIcon sx={{ fontSize: "0.9rem", color: Colors.MAIN_GRAY }} />
                            <Typography sx={{ fontSize: "0.92rem", color: Colors.MAIN_GRAY }}>
                              {order.lastEdited}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>

                    <Box
                      component="button"
                      onClick={(e: React.MouseEvent) => {
                        e.stopPropagation();
                        onViewOrder(order);
                      }}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.2rem",
                        fontSize: "1.14rem",
                        fontWeight: 600,
                        color: Colors.SECONDARY,
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        flexShrink: 0,
                      }}
                    >
                      View
                      <ChevronRightIcon sx={{ fontSize: "1.2rem" }} />
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {!primaryOrder && (
          <Typography sx={{ fontSize: "1.14rem", color: Colors.MAIN_GRAY, textAlign: "center", py: "1rem" }}>
            No pending orders.
          </Typography>
        )}

        {/* Footer actions */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: "1rem", mt: "0.5rem" }}>
          <CustomButton
            onClick={onClose}
            sx={{
              height: "4rem",
              px: "2rem",
              fontSize: "1.428rem",
              fontWeight: 600,
              borderRadius: "0.714285714rem",
              background: "none",
              color: Colors.MAIN_GRAY,
              border: `0.11429rem solid ${Colors.MAIN_GRAY}`,
              "&:hover": { background: Colors.BACKSHADE_GRAY },
            }}
          >
            Cancel
          </CustomButton>
          <CustomButton
            onClick={onClose}
            sx={{
              height: "4rem",
              px: "2rem",
              fontSize: "1.428rem",
              fontWeight: 600,
              borderRadius: "0.714285714rem",
            }}
          >
            Continue Working
          </CustomButton>
        </Box>
      </Box>
    </CustomModal>
  );
}
